import createQueryClient from "openapi-react-query";
import type { paths } from "../generated/api";
import { createRestomenuClient } from "./fetch";
import type { RestomenuClientOptions } from "./fetch";
import { toRestomenuError } from "./errors";

/**
 * Хуки кэша поверх транспорта.
 *
 * Граница подачи ошибки проходит здесь. `fetch.ts` ВОЗВРАЩАЕТ `{data, error}` —
 * родная форма openapi-fetch. TanStack Query распознаёт неудачу только по throw,
 * а рантайм openapi-react-query делает голое `if (error) throw error`. Это плохо
 * дважды. Во-первых, без прослойки наружу летел бы нетипизированный JSON
 * (а на 5xx — голая строка HTML). Во-вторых, `error` бывает falsy у настоящей
 * неудачи: `undefined` при `Content-Length: 0`, `""` при пустом теле без
 * заголовка — такой 401 сошёл бы за успех. Поэтому прослойка судит
 * по `!response.ok`, а не по наличию `error`.
 *
 * Прослойка перехватывает HTTP-методы клиента и конвертирует неудачу
 * в `RestomenuError` ДО того, как openapi-react-query её бросит.
 */
const HTTP_METHODS = new Set(["GET", "PUT", "POST", "DELETE", "OPTIONS", "HEAD", "PATCH", "TRACE"]);

export function createRestomenuQueryClient(options: RestomenuClientOptions) {
  const client = createRestomenuClient(options);

  // Оборачиваются ТОЛЬКО HTTP-методы: use/eject и остальная поверхность клиента
  // проходят насквозь — асинхронная обёртка сломала бы их контракт.
  const wrapped = new Proxy(client, {
    get(target, prop, receiver) {
      const original = Reflect.get(target, prop, receiver);
      if (typeof prop !== "string" || !HTTP_METHODS.has(prop) || typeof original !== "function") {
        return original;
      }

      return async (...args: unknown[]) => {
        const result = (await (original as (...a: unknown[]) => Promise<unknown>).apply(target, args)) as {
          data?: unknown;
          error?: unknown;
          response: Response;
        };
        if (!result.response.ok) {
          return { ...result, error: toRestomenuError(result.error, result.response) };
        }
        return result;
      };
    },
  });

  // Оба потребителя — хуки и императивные вызовы — делят ОДИН обёрнутый
  // клиент: single-flight 401 и конверсия в RestomenuError общие. Второй
  // экземпляр расщепил бы флаг, и пачка параллельных 401 будила бы
  // обработчик дважды. У fetchClient `error` при !response.ok — уже
  // RestomenuError (тип остаётся схемным Client<paths> — сужать через
  // instanceof на месте вызова).
  return { api: createQueryClient<paths>(wrapped), fetchClient: wrapped };
}
